import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";

/* ---------------------------------------------------------
   Design tokens — kept in sync with Dashboard.jsx /
   SitemapGroups.jsx / AddUrlForm.jsx.
--------------------------------------------------------- */
const COLOR = {
  bg: "#F5F6F4",
  surface: "#FFFFFF",
  ink: "#161A1D",
  inkMuted: "#6B7280",
  inkFaint: "#9AA1A9",
  line: "#E4E7E7",
  accent: "#0B7285",
  accentSoft: "#E3F1F1",
  danger: "#E0393B",
};

/* Centralized so a wrong guess about a route name is a
   one-line fix. groups is confirmed (shared with AddUrlForm);
   bulk is the new route you gave — confirm the base path
   ("/api/v1/sitemap") matches how it's actually mounted. */
const ENDPOINTS = {
  groups: (source) => `/api/v1/sitemap/groups?source=${source}`,
  bulkCreate: "/api/v1/sitemap/urls/bulk",
};

const PRIORITY_OPTIONS = [0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1];
const CHANGEFREQ_OPTIONS = [
  "always",
  "hourly",
  "daily",
  "weekly",
  "monthly",
  "yearly",
  "never",
];

// A soft ceiling so a pasted spreadsheet column of 20,000 rows
// doesn't get fired at the API in one request by accident.
const MAX_BULK_URLS = 500;

const DEFAULT_FORM = {
  group_id: "",
  priority: 0.5,
  changefreq: "weekly",
};

function isValidUrl(value) {
  try {
    const parsed = new URL(value);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

function parseUrls(text) {
  return text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
}

function validateBulkForm(form, urls, hasGroups) {
  const errors = {};

  if (hasGroups && !form.group_id) {
    errors.group_id = "Choose a sitemap group.";
  }

  if (!form.priority && form.priority !== 0) {
    errors.priority = "Priority is required.";
  }

  if (!form.changefreq) {
    errors.changefreq = "Change frequency is required.";
  }

  if (urls.length === 0) {
    errors.urls = "Add at least one URL, one per line.";
  } else if (urls.length > MAX_BULK_URLS) {
    errors.urls = `Max ${MAX_BULK_URLS} URLs per batch — you have ${urls.length}. Split this into smaller batches.`;
  } else {
    const invalidLines = urls
      .map((u, i) => ({ line: i + 1, url: u }))
      .filter((entry) => !isValidUrl(entry.url));

    if (invalidLines.length > 0) {
      const preview = invalidLines
        .slice(0, 3)
        .map((entry) => `line ${entry.line}`)
        .join(", ");
      const more = invalidLines.length > 3 ? ` and ${invalidLines.length - 3} more` : "";
      errors.urls = `${invalidLines.length} invalid URL${invalidLines.length > 1 ? "s" : ""} (${preview}${more}). Each must start with http:// or https://.`;
    }
  }

  return errors;
}

export default function AddBulkUrlForm({ source, onBack, onClose, onSuccess }) {
  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState(DEFAULT_FORM);
  const [urlsText, setUrlsText] = useState("");
  const [errors, setErrors] = useState({});

  useEffect(() => {
    let cancelled = false;

    const loadGroups = async () => {
      setLoadingGroups(true);
      try {
        const { data } = await axios.get(ENDPOINTS.groups(source));
        if (!cancelled) setGroups(data.data || []);
      } catch (error) {
        if (!cancelled) {
          toast.error(
            error.response?.data?.message || "Failed to load sitemap groups."
          );
        }
      } finally {
        if (!cancelled) setLoadingGroups(false);
      }
    };

    loadGroups();
    return () => {
      cancelled = true;
    };
  }, [source]);

  const hasGroups = groups.length > 0;

  const parsedUrls = useMemo(() => parseUrls(urlsText), [urlsText]);
  const uniqueUrls = useMemo(() => Array.from(new Set(parsedUrls)), [parsedUrls]);
  const duplicateCount = parsedUrls.length - uniqueUrls.length;
  const invalidCount = useMemo(
    () => uniqueUrls.filter((u) => !isValidUrl(u)).length,
    [uniqueUrls]
  );

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  };

  const handleUrlsChange = (e) => {
    setUrlsText(e.target.value);
    setErrors((prev) => (prev.urls ? { ...prev, urls: undefined } : prev));
  };

  const save = async () => {
    const validationErrors = validateBulkForm(form, uniqueUrls, hasGroups);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    setSaving(true);
    try {
      const { data } = await axios.post(ENDPOINTS.bulkCreate, {
        group_id: form.group_id,
        source,
        changefreq: form.changefreq,
        priority: form.priority,
        urls: uniqueUrls,
      });
      toast.success(data?.message || `${uniqueUrls.length} URLs added successfully.`);
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to add URLs.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <DialogTitle sx={{ fontWeight: 600, color: COLOR.ink }}>
        {source === "wordpress" ? "Bulk add WordPress URLs" : "Bulk add custom URLs"}
      </DialogTitle>

      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          {!loadingGroups && !hasGroups && (
            <Alert severity="warning" sx={{ borderRadius: "8px" }}>
              No sitemap groups exist for this source yet. Create a group first,
              then add URLs to it.
            </Alert>
          )}

          <TextField
            select
            fullWidth
            label="Sitemap group"
            value={form.group_id}
            onChange={(e) => updateField("group_id", e.target.value)}
            error={!!errors.group_id}
            helperText={errors.group_id}
            disabled={loadingGroups || !hasGroups || saving}
            SelectProps={{
              displayEmpty: true,
              renderValue: (value) => {
                if (loadingGroups) return "Loading groups…";
                if (!value) return "Select a group";
                const match = groups.find((g) => g.id === value);
                return match ? `${match.name} (${match.url_count}/${match.chunk_size})` : value;
              },
            }}
          >
            {groups.map((g) => (
              <MenuItem key={g.id} value={g.id} disabled={g.is_full}>
                {g.name} ({g.url_count}/{g.chunk_size})
                {g.is_full ? " — full" : ""}
              </MenuItem>
            ))}
          </TextField>

          <Box>
            <TextField
              fullWidth
              multiline
              minRows={6}
              maxRows={10}
              label="URLs"
              placeholder={"https://example.com/\nhttps://example.com/about\nhttps://example.com/contact"}
              value={urlsText}
              onChange={handleUrlsChange}
              error={!!errors.urls}
              helperText={errors.urls || "One URL per line."}
              disabled={saving}
              sx={{ "& textarea": { fontSize: "13px" } }}
            />

            {parsedUrls.length > 0 && (
              <Box display="flex" alignItems="center" gap="8px" flexWrap="wrap" mt="8px">
                <Chip
                  size="small"
                  label={`${uniqueUrls.length} URL${uniqueUrls.length !== 1 ? "s" : ""}`}
                  sx={{ bgcolor: COLOR.accentSoft, color: COLOR.accent, fontWeight: 500, fontSize: "12px" }}
                />
                {duplicateCount > 0 && (
                  <Chip
                    size="small"
                    label={`${duplicateCount} duplicate${duplicateCount !== 1 ? "s" : ""} skipped`}
                    sx={{ bgcolor: COLOR.bg, color: COLOR.inkMuted, fontWeight: 500, fontSize: "12px" }}
                  />
                )}
                {invalidCount > 0 && (
                  <Chip
                    size="small"
                    label={`${invalidCount} look${invalidCount === 1 ? "s" : ""} invalid`}
                    sx={{ bgcolor: "#FCEAEA", color: COLOR.danger, fontWeight: 500, fontSize: "12px" }}
                  />
                )}
              </Box>
            )}
          </Box>

          <Box display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={2}>
            <TextField
              select
              fullWidth
              label="Priority"
              value={form.priority}
              onChange={(e) => updateField("priority", Number(e.target.value))}
              error={!!errors.priority}
              helperText={errors.priority || "Applied to every URL in this batch."}
              disabled={saving}
            >
              {PRIORITY_OPTIONS.map((p) => (
                <MenuItem key={p} value={p}>
                  {p.toFixed(1)}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              fullWidth
              label="Change frequency"
              value={form.changefreq}
              onChange={(e) => updateField("changefreq", e.target.value)}
              error={!!errors.changefreq}
              helperText={errors.changefreq || "Applied to every URL in this batch."}
              disabled={saving}
            >
              {CHANGEFREQ_OPTIONS.map((f) => (
                <MenuItem key={f} value={f} sx={{ textTransform: "capitalize" }}>
                  {f}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onBack} disabled={saving} sx={{ textTransform: "none", color: COLOR.inkMuted }}>
          Back
        </Button>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: "none", color: COLOR.inkMuted }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disableElevation
          onClick={save}
          disabled={saving || (!loadingGroups && !hasGroups)}
          sx={{
            bgcolor: COLOR.accent,
            textTransform: "none",
            fontWeight: 600,
            "&:hover": { bgcolor: "#095e6b" },
          }}
        >
          {saving ? (
            <CircularProgress size={18} sx={{ color: "#fff" }} />
          ) : (
            `Add ${uniqueUrls.length || ""} URL${uniqueUrls.length === 1 ? "" : "s"}`.trim()
          )}
        </Button>
      </DialogActions>
    </>
  );
}